'use client'

import { html } from '@ensemble.music/ui'
import { MotionConfig } from 'motion/react'
import { useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useEffect } from 'react'
import { Benefits } from '../landingpage/Benefits'
import { EventTypes } from '../landingpage/EventTypes'
import { FaqSection, faqJsonLd } from '../landingpage/FaqSection'
import { FinalCta } from '../landingpage/FinalCta'
import { FooterLandingPage } from '../landingpage/Footer'
import { Hero } from '../landingpage/Hero'
import { HowItWorks } from '../landingpage/HowItWorks'
import { Navbar } from '../landingpage/Navbar'
import { References } from '../landingpage/References'
import { StickyMobileCta } from '../landingpage/StickyMobileCta'

const requestHref = (occasion: string) =>
  occasion ? `/buchen/anfrage?anlass=${encodeURIComponent(occasion)}` : '/buchen/anfrage'

export function BuchenScreen() {
  const router = useRouter()
  const params = useSearchParams()

  // Every CTA leads to the request form on its own page; an occasion chip or card preselects it.
  const start = useCallback((picked = '') => router.push(requestHref(picked)), [router])

  // Old deep links (?anfrage=1 opened the modal) still land on the form.
  useEffect(() => {
    if (params.get('anfrage') === '1') router.replace(requestHref(params.get('anlass') ?? ''))
  }, [params, router])

  return (
    // reducedMotion="user" is the single switch for every motion component on the page: under
    // prefers-reduced-motion transforms are dropped and only opacity remains.
    <MotionConfig reducedMotion="user">
      <html.div className="flex flex-col min-h-screen bg-background">
        <Navbar onStart={start} />

        <html.main
          id="inhalt"
          className="flex flex-col grow"
        >
          <html.div
            id="hero"
            className="w-full max-w-[1120px] mx-auto px-8 pt-16 pb-32 max-sm:px-4 max-sm:pt-10 max-sm:pb-20"
          >
            <Hero onStart={start} />
          </html.div>

          {/* The air between the blocks is the design device from STYLE.md: 160 px on a desktop,
              96 px on a phone, so every section arrives on its own instead of in a wall. */}
          <html.div className="flex flex-col gap-40 w-full max-w-[1120px] mx-auto px-8 pb-40 max-sm:px-4 max-sm:gap-24 max-sm:pb-24">
            <HowItWorks />
            <EventTypes onPick={start} />
            <Benefits />
            <References />
            <FaqSection />
          </html.div>

          <html.div id="final-cta">
            <FinalCta onStart={() => start()} />
          </html.div>
        </html.main>

        <FooterLandingPage />
        <StickyMobileCta
          heroId="hero"
          finalCtaId="final-cta"
          hidden={false}
          onStart={() => start()}
        />

        {/* Same array as the accordion above, so the structured data cannot drift from the page. */}
        <script
          type="application/ld+json"
          // biome-ignore lint/security/noDangerouslySetInnerHtml: JSON-LD has no other way in
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd()) }}
        />
      </html.div>
    </MotionConfig>
  )
}
